import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, Wine, DollarSign, Calendar, Sparkles, ChevronRight, ChevronLeft, Users, Eye, Info, Calculator, CalendarDays, LayoutGrid, List, AlertCircle, TrendingDown, Coins, UserPlus, AlertTriangle, X, ChevronDown } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useToast } from '@/components/ui/use-toast';
import { apiClient } from '@/services/api/apiClient';
import Cookies from 'js-cookie';
import HeatGaugeMeters from '@/components/HeatGaugeMeters';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface MealJourneyItem {
  name: string;
  price: number;
  section: string;
  item_id?: number;
  image?: string | null;
}

interface MealJourney {
  item1: MealJourneyItem;
  item2: MealJourneyItem;
  item3: MealJourneyItem;
  frequency: number;
  percentage: number;
  total_value: number;
}

interface DrinkPairing {
  drink_name: string;
  drink_price: number;
  food_name: string;
  food_price?: number;
  order_count: number;  // Actual orders for the selected period
  revenue_per_order: number;
  total_revenue: number;
  attach_percentage?: number;
  lift_score?: number;
  attachment_potential?: number;
  opportunity_score?: number;
  category?: string;
  potential_revenue_increase?: number;
  weekly_orders?: number;  // DEPRECATED - use order_count
  drink_image?: string | null;
  food_image?: string | null;
}

interface PriceRange {
  label: string;
  min_price: number;
  max_price: number;
  order_count: number;
  percentage: number;
  recommendation?: string;
}

interface ConstellationItem {
  item_id: number;
  name: string;
  price: number;
  section: string;
  order_count: number;
  price_range: string;
  popularity: number;
  image_url?: string | null;
}

interface ConstellationConnection {
  source_id: number;
  target_id: number;
  frequency: number;
  lift_score: number;
  source_name: string;
  target_name: string;
}

interface InsightsData {
  journeys: MealJourney[];
  pairings: DrinkPairing[];
  distribution: PriceRange[];
  total_orders?: number;
  total_drink_revenue?: number;
  average_order_value?: number;
  sweet_spot?: string;
  period_label?: string;  // 'week', 'month', or 'quarter'
  date_range_start?: string;
  date_range_end?: string;
  last_updated?: string;
  days_since_update?: number;
  fallback_used?: boolean;
  data_age_days?: number;
  constellation_data?: {
    items: ConstellationItem[];
    connections: ConstellationConnection[];
    combo_insights?: {
      [key: string]: {
        Entrees: Array<{ name: string; price: number; order_count: number }>;
        Appetizers: Array<{ name: string; price: number; order_count: number }>;
        Drinks: Array<{ name: string; price: number; order_count: number }>;
      };
    };
  };
}

const CustomerPreferenceInsights = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null); // null = checking, false = no access, true = has access
  const [days, setDays] = useState(7);  // Default to Last Week
  const [drinkPairingView, setDrinkPairingView] = useState<'cards' | 'list'>('cards');
  const [currentDrinkIndex, setCurrentDrinkIndex] = useState(0);
  const [currentJourneyIndex, setCurrentJourneyIndex] = useState(0);  // Add state for journey navigation
  const [improvementPercent, setImprovementPercent] = useState(5); // Default 5%
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [selectedPairing, setSelectedPairing] = useState<DrinkPairing | null>(null);
  const [data, setData] = useState<InsightsData>({
    journeys: [],
    pairings: [],
    distribution: []
  });

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (hasAccess) {
      fetchInsights();
    }
  }, [days, hasAccess]);

  // Update default improvement percentage based on time period
  useEffect(() => {
    if (days <= 7) {
      setImprovementPercent(10); // Weekly: 10%
    } else if (days <= 30) {
      setImprovementPercent(5); // Monthly: 5%
    } else {
      setImprovementPercent(2); // Quarterly: 2%
    }
  }, [days]);

  const checkAccess = async () => {
    try {
      // Don't pass Authorization header - apiClient already adds it
      const response = await apiClient.get('/customer-insights/check-access');
      
      setHasAccess(response.data.has_access);
      if (!response.data.has_access) {
        toast({
          title: 'Premium Feature',
          description: response.data.message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const fetchInsights = async () => {
    setLoading(true);
    try {
      // Fetch all three insights in parallel
      const [journeysRes, pairingsRes, distributionRes] = await Promise.all([
        apiClient.get(`/customer-insights/meal-journeys?days=${days}`),
        apiClient.get(`/customer-insights/drink-pairings?days=${days}`),
        apiClient.get(`/customer-insights/price-distribution?days=${days}`)
      ]);

      // Debug logging
      console.log(`Data for ${days} days:`, {
        firstJourney: journeysRes.data.journeys?.[0],
        journeyCount: journeysRes.data.journeys?.length
      });
      
      // Debug drink pairing data
      console.log('Drink pairings data:', pairingsRes.data);
      if (pairingsRes.data.pairings && pairingsRes.data.pairings.length > 0) {
        console.log('First pairing:', pairingsRes.data.pairings[0]);
      }
      
      // Debug constellation data
      console.log('Constellation data:', distributionRes.data.constellation_data);
      
      setData({
        journeys: journeysRes.data.journeys || [],
        pairings: pairingsRes.data.pairings || [],
        distribution: distributionRes.data.distribution || [],
        total_orders: journeysRes.data.total_orders,
        total_drink_revenue: pairingsRes.data.total_drink_revenue,
        average_order_value: distributionRes.data.average_order_value,
        sweet_spot: distributionRes.data.sweet_spot,
        // Add timestamp data - prioritize pairings data as it's the main feature
        period_label: pairingsRes.data.period_label || journeysRes.data.period_label,
        date_range_start: pairingsRes.data.date_range_start || journeysRes.data.date_range_start,
        date_range_end: pairingsRes.data.date_range_end || journeysRes.data.date_range_end,
        last_updated: pairingsRes.data.last_updated || journeysRes.data.last_updated,
        days_since_update: pairingsRes.data.days_since_update ?? journeysRes.data.days_since_update,
        fallback_used: pairingsRes.data.fallback_used || journeysRes.data.fallback_used,
        data_age_days: pairingsRes.data.data_age_days || journeysRes.data.data_age_days,
        // Add constellation data from price distribution endpoint
        constellation_data: distributionRes.data.constellation_data
      });
    } catch (error: any) {
      console.error('Error fetching insights:', error);
      // Check if it's a 404 or empty data scenario (expected for new users)
      const isEmptyData = error?.response?.status === 404 || 
                         error?.response?.data?.detail?.includes('No data') ||
                         error?.response?.data?.detail?.includes('No orders');
      
      if (!isEmptyData) {
        // Only show error toast for actual errors, not empty data
        toast({
          title: 'Error',
          description: 'Failed to load customer insights',
          variant: 'destructive'
        });
      }
      
      // Set empty data state for new users
      setData({
        journeys: [],
        pairings: [],
        distribution: [],
        total_orders: 0,
        total_drink_revenue: 0,
        average_order_value: 0,
        sweet_spot: null,
        period_label: 'week',
        date_range_start: null,
        date_range_end: null
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined || isNaN(price)) {
      return '$0.00';
    }
    return `$${price.toFixed(2)}`;
  };

  // Find max percentage for chart scaling
  const maxPercentage = Math.max(...data.distribution.map(d => d.percentage), 1);

  // Calculate revenue impact based on improvement percentage
  const calculateRevenueImpact = (orderCount: number, drinkPrice: number) => {
    const additionalOrders = orderCount * (improvementPercent / 100);
    return additionalOrders * drinkPrice;
  };

  // Get period label from backend data
  const getPeriodLabel = () => {
    if (data.period_label) {
      return data.period_label.charAt(0).toUpperCase() + data.period_label.slice(1);
    }
    // Fallback
    if (days <= 7) return 'Weekly';
    if (days <= 30) return 'Monthly';
    return 'Quarterly';
  };

  const periodLabel = getPeriodLabel();

  // Show loading while checking access
  if (hasAccess === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913]" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-4xl mx-auto mt-20">
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-200 shadow-lg">
            <Users className="h-16 w-16 text-[#FDB913] mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Customer Preference Insights
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              Unlock powerful insights about your customers' dining preferences, popular combinations, and spending patterns. Available for Delicious and Gourmet plans.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className="bg-[#FDB913] text-black px-8 py-3 rounded-full font-bold hover:bg-[#e5a811] transition-all"
            >
              Upgrade Your Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/restaurant-dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              Customer Preference Insights
            </h1>
            <Select value={days.toString()} onValueChange={(value) => setDays(parseInt(value))}>
              <SelectTrigger className="w-44 bg-white border-gray-300 text-gray-900">
                <Calendar className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last Week</SelectItem>
                <SelectItem value="30">Last Month</SelectItem>
                <SelectItem value="90">Last Quarter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913]" />
        </div>
      ) : (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          {/* Data Freshness Warning */}
          {data.fallback_used && data.data_age_days && data.data_age_days > 7 && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-gray-900 font-medium mb-1">
                  Showing historical data from {new Date(data.date_range_start!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(data.date_range_end!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
                <p className="text-gray-600 text-sm">
                  No recent data available for the selected period. This data is approximately {data.data_age_days} days old.
                  {data.data_age_days > 30 && ' Consider running a basket analysis refresh to get current insights.'}
                </p>
              </div>
            </div>
          )}
          {/* Perfect Meal Journeys */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-[#FDB913]/20 p-3 rounded-xl mr-4">
                  <TrendingUp className="h-6 w-6 text-[#FDB913]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-900">Perfect Meal Journeys</h2>
                  </div>
                  <p className="text-gray-600">Your customers' favorite dining experiences</p>
                </div>
              </div>
              {data.date_range_start && data.date_range_end && (
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
                  <Calendar className="w-4 h-4" />
                  <span className="flex items-center gap-2">
                    <span className="text-gray-400">Last Refresh:</span>{' '}
                    <span>
                      {new Date(data.date_range_start).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })} - {new Date(data.date_range_end).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    {data.days_since_update !== undefined && data.days_since_update !== null && data.days_since_update > 7 && (
                      <div className="relative group">
                        <Info className="w-4 h-4 text-red-500 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-64">
                          <div>
                            Showing data from {new Date(data.date_range_start).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}-{new Date(data.date_range_end).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}, {new Date(data.date_range_end).getFullYear()}.
                            <br />
                            We are aware of the issue and are actively working to resolve this asap.
                          </div>
                          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    )}
                  </span>
                </div>
              )}
            </div>

            {data.journeys.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center border border-gray-200 shadow-sm">
                <div className="max-w-2xl mx-auto">
                  <div className="mb-4">
                    <div className="w-16 h-16 bg-[#FDB913]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="h-8 w-8 text-[#FDB913]" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Building Journey Patterns</h3>
                    <p className="text-gray-600 mb-6">
                      Perfect meal journeys appear when customers consistently order the same 3 items together.
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4 text-left">
                    <p className="text-gray-500 text-sm mb-3">
                      <span className="font-semibold text-gray-700">Current status:</span> Insights will appear after your first week of data collection (Sunday to Saturday)
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <span className="text-[#FDB913] mt-1">•</span>
                        <span className="text-gray-500">Most popular 3-item combination has only 3 orders (need 5+ for pattern recognition)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#FDB913] mt-1">•</span>
                        <span className="text-gray-500">Many orders include 3+ items, but with varying combinations</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-[#FDB913] mt-1">•</span>
                        <span className="text-gray-500">As repeat patterns emerge, they'll automatically appear here</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6">
                    <p className="text-gray-400 text-xs">
                      Tip: Consider promoting popular combinations to encourage consistent ordering patterns
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-3 gap-6">
                  {data.journeys.slice(0, 3).map((journey, index) => {
                  // Use the actual frequency from the journey data
                  const customerCount = journey.count || journey.frequency || 0;

                  return (
                    <motion.div
                      key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                    className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-[#FDB913]/50 transition-all hover:shadow-lg group"
                  >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#FDB913]/10 to-orange-400/10 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl font-bold text-[#FDB913]">#{index + 1}</span>
                          {index === 0 && (
                            <span className="bg-[#FDB913]/20 text-[#b8860b] text-xs font-semibold px-2 py-1 rounded-full">
                              Hidden Bestseller
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-gray-900 font-semibold">{customerCount} customers</p>
                          <p className="text-gray-500 text-xs">ordered this {days === 7 ? 'last week' : days === 30 ? 'last month' : 'last quarter'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Journey Path */}
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        {/* Item 1 */}
                        <div className="flex-1 text-center">
                          <div className="w-20 h-20 mx-auto mb-2 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200">
                            {journey.item1.image ? (
                              <img src={journey.item1.image} alt={journey.item1.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-2xl">🍽️</span>
                              </div>
                            )}
                          </div>
                          <div className="h-10 flex flex-col justify-center">
                            <p className="text-gray-900 font-medium text-sm leading-tight px-1 line-clamp-2">{journey.item1.name}</p>
                          </div>
                          <p className="text-gray-500 text-sm">{formatPrice(journey.item1.price)}</p>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center px-1 flex-shrink-0">
                          <div className="w-6 h-0.5 bg-gradient-to-r from-gray-200 to-[#FDB913]/40"></div>
                          <motion.div
                            animate={{ x: [0, 3, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                          >
                            <ChevronRight className="h-5 w-5 text-[#FDB913]/60 -ml-1" />
                          </motion.div>
                        </div>

                        {/* Item 2 */}
                        <div className="flex-1 text-center">
                          <div className="w-20 h-20 mx-auto mb-2 bg-gray-100 rounded-xl overflow-hidden border-2 border-gray-200">
                            {journey.item2.image ? (
                              <img src={journey.item2.image} alt={journey.item2.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Wine className="h-8 w-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="h-10 flex flex-col justify-center">
                            <p className="text-gray-900 font-medium text-sm leading-tight px-1 line-clamp-2">{journey.item2.name}</p>
                          </div>
                          <p className="text-gray-500 text-sm">{formatPrice(journey.item2.price)}</p>
                        </div>

                        {/* Arrow */}
                        <div className="flex items-center px-1 flex-shrink-0">
                          <div className="w-6 h-0.5 bg-gradient-to-r from-[#FDB913]/40 to-[#FDB913]/60"></div>
                          <motion.div
                            animate={{ x: [0, 3, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                          >
                            <ChevronRight className="h-5 w-5 text-[#FDB913] -ml-1" />
                          </motion.div>
                        </div>

                        {/* Item 3 */}
                        <div className="flex-1 text-center">
                          <div className="w-20 h-20 mx-auto mb-2 bg-gray-100 rounded-xl overflow-hidden border-2 border-[#FDB913]/50">
                            {journey.item3.image ? (
                              <img src={journey.item3.image} alt={journey.item3.name} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-2xl">🍽️</span>
                              </div>
                            )}
                          </div>
                          <div className="h-10 flex flex-col justify-center">
                            <p className="text-gray-900 font-medium text-sm leading-tight px-1 line-clamp-2">{journey.item3.name}</p>
                          </div>
                          <p className="text-gray-500 text-sm">{formatPrice(journey.item3.price)}</p>
                        </div>
                      </div>

                      {/* Pricing Story */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-gray-600 text-sm">Current total:</span>
                          <span className="text-xl font-bold text-gray-900">{formatPrice((journey.item1.price || 0) + (journey.item2.price || 0) + (journey.item3.price || 0))}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-3">
                          <p className="text-gray-500 text-xs leading-relaxed">
                            💡 Consider offering as a discounted combo if these items have healthy margins.
                            Even a small discount can incentivize this popular combination.
                          </p>
                        </div>
                      </div>

                      {/* CTA */}
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        className="w-full bg-[#FDB913] hover:bg-[#e5a811] text-gray-900 font-bold py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 group shadow-md hover:shadow-lg"
                      >
                        <motion.span
                          className="text-2xl"
                          animate={{ rotate: [0, -10, 10, 0] }}
                          transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                        >
                          🚀
                        </motion.span>
                        Launch This Combo
                      </motion.button>
                    </div>
                  </motion.div>
                );
              })}
                </div>
              </>
            )}
          </motion.div>

          {/* Drink Pairing Winners */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-[#FDB913]/20 p-3 rounded-xl mr-4">
                  <Wine className="h-6 w-6 text-[#FDB913]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-900">Drink Pairing Winners</h2>
                  </div>
                  <p className="text-gray-600">High-margin opportunities</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {data.date_range_start && data.date_range_end && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
                    <Calendar className="w-4 h-4" />
                    <span className="flex items-center gap-2">
                      <span className="text-gray-400">Last Refresh:</span>{' '}
                      <span>
                        {new Date(data.date_range_start).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })} - {new Date(data.date_range_end).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      {data.days_since_update !== undefined && data.days_since_update !== null && data.days_since_update > 7 && (
                        <div className="relative group">
                          <Info className="w-4 h-4 text-red-500 cursor-help" />
                          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-64">
                            <div>
                              Showing data from {new Date(data.date_range_start).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}-{new Date(data.date_range_end).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}, {new Date(data.date_range_end).getFullYear()}.
                              <br />
                              We are aware of the issue and are actively working to resolve this asap.
                            </div>
                            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      )}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => setDrinkPairingView(drinkPairingView === 'cards' ? 'list' : 'cards')}
                  className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 transition-all"
                  title={drinkPairingView === 'cards' ? 'Switch to List View' : 'Switch to Card View'}
                >
                  {drinkPairingView === 'cards' ? (
                    <List className="h-5 w-5" />
                  ) : (
                    <LayoutGrid className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              {data.pairings.length === 0 ? (
                <div className="text-center py-12">
                  <Wine className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">No drink pairing data available for this time period.</p>
                  <p className="text-gray-400 text-sm mt-2">As more orders come in, we'll identify high-margin pairing opportunities.</p>
                </div>
              ) : drinkPairingView === 'cards' ? (
                <div className="relative">
                  {/* Card View */}
                  <div className="grid grid-cols-3 gap-4">
                    {data.pairings.slice(0, 3).map((pairing, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.02, y: -5 }}
                        className={`relative group ${
                          selectedPairing &&
                          selectedPairing.drink_name === pairing.drink_name &&
                          selectedPairing.food_name === pairing.food_name &&
                          isCalculatorOpen
                            ? 'ring-2 ring-[#FDB913] ring-offset-2 ring-offset-white'
                            : ''
                        }`}
                      >
                        {/* Rank Badge */}
                        <div className="absolute top-2 left-2 z-10 bg-gradient-to-r from-[#FDB913] to-orange-400 text-gray-900 font-bold text-sm px-2 py-1 rounded-full">
                          #{index + 1}
                        </div>

                        {/* Revenue Potential Badge */}
                        {pairing.order_count && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3 + index * 0.1, type: "spring", stiffness: 200 }}
                            className="absolute top-2 right-2 z-10 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full group-hover:bg-green-600 transition-colors"
                          >
                            +${calculateRevenueImpact(pairing.order_count, pairing.drink_price).toFixed(0)}/{data.period_label || 'period'}
                          </motion.div>
                        )}

                        {/* Card */}
                        <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-[#FDB913]/50 transition-all group-hover:shadow-lg">
                          {/* Drink Image Container */}
                          <div className="relative h-48 bg-gradient-to-b from-gray-100 to-gray-50">
                            {/* Use AI-generated image if available, otherwise use emoji icons */}
                            {pairing.drink_image ? (
                              <img
                                src={pairing.drink_image}
                                alt={pairing.drink_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                {pairing.drink_name.toLowerCase().includes('margarita') ||
                                 pairing.drink_name.toLowerCase().includes('cocktail') ? (
                                  <div className="text-6xl">🍹</div>
                                ) : pairing.drink_name.toLowerCase().includes('wine') ||
                                     pairing.drink_name.toLowerCase().includes('sangria') ? (
                                  <div className="text-6xl">🍷</div>
                                ) : pairing.drink_name.toLowerCase().includes('beer') ? (
                                  <div className="text-6xl">🍺</div>
                                ) : pairing.drink_name.toLowerCase().includes('mojito') ||
                                     pairing.drink_name.toLowerCase().includes('gin') ||
                                     pairing.drink_name.toLowerCase().includes('fizz') ? (
                                  <div className="text-6xl">🥃</div>
                                ) : pairing.drink_name.toLowerCase().includes('bellini') ||
                                     pairing.drink_name.toLowerCase().includes('spritz') ? (
                                  <div className="text-6xl">🥂</div>
                                ) : (
                                  <Wine className="h-20 w-20 text-gray-300" />
                                )}
                              </div>
                            )}

                            {/* Food Label Overlay */}
                            <div className="absolute bottom-3 left-3 right-3">
                              <div className="bg-gray-900/80 backdrop-blur-sm rounded-lg px-3 py-2">
                                <p className="text-gray-400 text-xs">Perfect with</p>
                                <p className="text-white font-semibold text-sm">{pairing.food_name}</p>
                              </div>
                            </div>
                          </div>

                          {/* Card Content */}
                          <div className="p-4 bg-white">
                            <h4 className="text-gray-900 font-semibold text-lg mb-1">{pairing.drink_name}</h4>
                            <p className="text-[#FDB913] font-medium mb-3">{formatPrice(pairing.drink_price)}</p>

                            <div className="space-y-2 text-sm">
                              {/* Lift Score - New! */}
                              {pairing.lift_score && (
                                <div className="bg-purple-50 rounded-lg p-2 border border-purple-100">
                                  <p className="text-purple-600 font-semibold text-xs mb-1">True Affinity</p>
                                  <p className="text-gray-900 font-bold">{pairing.lift_score.toFixed(1)}x</p>
                                  <p className="text-gray-500 text-xs">more likely together</p>
                                </div>
                              )}
                              <div className="bg-green-50 rounded-lg p-2 border border-green-100">
                                <p className="text-green-600 font-semibold text-xs mb-1">Current Attachment</p>
                                <p className="text-gray-900 font-bold">{pairing.attach_percentage ? `${pairing.attach_percentage.toFixed(1)}%` : 'N/A'}</p>
                                <p className="text-gray-500 text-xs">of {pairing.food_name} orders</p>
                              </div>
                              {pairing.order_count && (
                                <div className="bg-[#FDB913]/10 rounded-lg p-3 relative border border-[#FDB913]/30 hover:border-[#FDB913]/50 transition-all">
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-[#b8860b] font-semibold text-sm">{periodLabel} Performance</p>
                                    <button
                                      onClick={() => {
                                        setSelectedPairing(pairing);
                                        setIsCalculatorOpen(true);
                                      }}
                                      className="bg-[#FDB913]/20 hover:bg-[#FDB913]/30 p-2 rounded-lg transition-all group"
                                      title="Open Revenue Calculator"
                                    >
                                      <Calculator className="h-5 w-5 text-[#b8860b] group-hover:scale-110 transition-transform" />
                                    </button>
                                  </div>
                                  <p className="text-gray-500 text-xs">
                                    {pairing.order_count} orders/{data.period_label || 'period'}
                                  </p>
                                  <p className="text-gray-900 font-bold">
                                    +{formatPrice(calculateRevenueImpact(pairing.order_count, pairing.drink_price))}
                                    <span className="text-gray-500 text-xs font-normal">/{data.period_label || 'period'} potential</span>
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="mt-4 pt-3 border-t border-gray-200">
                              <p className="text-gray-500 text-xs leading-relaxed">
                                💡 Train servers to suggest {pairing.drink_name} with {pairing.food_name}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ) : (
                /* List View */
                <div className="space-y-3">
                  {data.pairings.slice(0, 3).map((pairing, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`flex items-center justify-between p-4 rounded-lg transition-all ${
                        selectedPairing &&
                        selectedPairing.drink_name === pairing.drink_name &&
                        selectedPairing.food_name === pairing.food_name &&
                        isCalculatorOpen
                          ? 'bg-[#FDB913]/10 border-2 border-[#FDB913]'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="bg-[#FDB913]/20 text-[#b8860b] font-bold text-sm w-8 h-8 rounded-full flex items-center justify-center">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Wine className="h-4 w-4 text-[#FDB913]" />
                            <span className="text-gray-900 font-medium">{pairing.drink_name}</span>
                            <span className="text-gray-400">+</span>
                            <span className="text-gray-700">{pairing.food_name}</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm">
                            <span className="text-gray-500">
                              {pairing.attach_percentage ? `${pairing.attach_percentage.toFixed(0)}%` : '0%'} of {pairing.food_name} orders
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="text-[#b8860b]">${pairing.drink_price?.toFixed(2) || '0.00'} per drink</span>
                            {pairing.lift_score && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-purple-600">{pairing.lift_score.toFixed(1)}x affinity</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {pairing.order_count && (
                          <div className="text-right">
                            <div className="text-gray-500 text-sm mb-1">
                              {pairing.order_count} orders/{data.period_label || 'period'}
                            </div>
                            <div className="bg-[#FDB913]/10 rounded-lg px-3 py-2 border border-[#FDB913]/30">
                              <div className="text-[#b8860b] font-bold">
                                +{formatPrice(calculateRevenueImpact(pairing.order_count, pairing.drink_price))}/{data.period_label || 'period'}
                              </div>
                              <div className="text-xs text-gray-500">at {improvementPercent}% improvement</div>
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setSelectedPairing(pairing);
                            setIsCalculatorOpen(true);
                          }}
                          className="bg-[#FDB913]/20 hover:bg-[#FDB913]/30 p-3 rounded-lg transition-all group flex flex-col items-center gap-1"
                          title="Open Revenue Calculator"
                        >
                          <Calculator className="h-6 w-6 text-[#b8860b] group-hover:scale-110 transition-transform" />
                          <span className="text-xs text-[#b8860b] font-medium">Calculate</span>
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>


          {/* Price Constellation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-[#FDB913]/20 p-3 rounded-xl mr-4">
                  <Sparkles className="h-6 w-6 text-[#FDB913]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-900">Price Sweet Spots</h2>
                  </div>
                  <p className="text-gray-600">See which price ranges are hot and which need attention</p>
                </div>
              </div>
              {data.date_range_start && data.date_range_end && (
                <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg">
                  <Calendar className="w-4 h-4" />
                  <span className="flex items-center gap-2">
                    <span className="text-gray-400">Last Refresh:</span>{' '}
                    <span>
                      {new Date(data.date_range_start).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })} - {new Date(data.date_range_end).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                    {data.days_since_update !== undefined && data.days_since_update !== null && data.days_since_update > 7 && (
                      <div className="relative group">
                        <Info className="w-4 h-4 text-red-500 cursor-help" />
                        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-64">
                          <div>
                            Showing data from {new Date(data.date_range_start).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}-{new Date(data.date_range_end).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}, {new Date(data.date_range_end).getFullYear()}.
                            <br />
                            We are aware of the issue and are actively working to resolve this asap.
                          </div>
                          <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm overflow-hidden">
              {/* Heat Gauge Visualization */}
              {data.constellation_data ? (
                <HeatGaugeMeters
                  items={data.constellation_data.items}
                  distribution={data.distribution}
                  comboInsights={data.constellation_data.combo_insights}
                />
              ) : (
                <div className="text-center py-12">
                  <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Loading price analysis...</p>
                </div>
              )}
            </div>
          </motion.div>

        </div>
      )}

      {/* Revenue Impact Calculator Modal */}
      <Dialog open={isCalculatorOpen} onOpenChange={setIsCalculatorOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl shadow-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <div className="bg-[#FDB913]/20 p-2 rounded-lg">
                <Calculator className="h-6 w-6 text-[#FDB913]" />
              </div>
              Revenue Impact Calculator
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedPairing && (
                <span>
                  Calculate {periodLabel.toLowerCase()} revenue potential for {selectedPairing.drink_name} + {selectedPairing.food_name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedPairing && (
            <div className="space-y-6 py-4">
              {/* Pairing Being Calculated */}
              <div className="bg-[#FDB913]/10 rounded-lg p-3 border border-[#FDB913]/30 flex items-center gap-3">
                <Wine className="h-5 w-5 text-[#FDB913]" />
                <div>
                  <p className="text-gray-900 font-semibold">
                    {selectedPairing.drink_name} + {selectedPairing.food_name}
                  </p>
                  <p className="text-gray-600 text-sm">Calculating revenue potential for this pairing</p>
                </div>
              </div>

              {/* Current Performance */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-gray-900 font-medium mb-3">Current {periodLabel} Performance</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">{periodLabel} Orders</p>
                    <p className="text-gray-900 font-bold text-lg">{selectedPairing.order_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Drink Price</p>
                    <p className="text-[#FDB913] font-bold text-lg">{formatPrice(selectedPairing.drink_price)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Current Attachment</p>
                    <p className="text-gray-900 font-bold text-lg">{selectedPairing.attach_percentage?.toFixed(1) || '0'}%</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Lift Score</p>
                    <p className="text-gray-900 font-bold text-lg">{selectedPairing.lift_score?.toFixed(1) || '1.0'}x</p>
                  </div>
                </div>
              </div>

              {/* Improvement Slider */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-gray-900 font-medium">Improvement Target</h4>
                  <span className="text-[#FDB913] font-bold text-xl">
                    {improvementPercent}%
                  </span>
                </div>
                <div className="relative">
                  <input
                    type="range"
                    min="2"
                    max="20"
                    step="1"
                    value={improvementPercent}
                    onChange={(e) => setImprovementPercent(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    style={{
                      background: `linear-gradient(to right, #FDB913 0%, #FDB913 ${(improvementPercent - 2) / 18 * 100}%, #e5e7eb ${(improvementPercent - 2) / 18 * 100}%, #e5e7eb 100%)`
                    }}
                  />
                  <div className="flex justify-between mt-2 text-xs text-gray-400">
                    <span>2%</span>
                    <span>5%</span>
                    <span>10%</span>
                    <span>15%</span>
                    <span>20%</span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm mt-3">
                  {improvementPercent <= 5 ? '🎯 Conservative: Focus on staff training and gentle upselling' :
                   improvementPercent <= 10 ? '💪 Achievable: Run targeted promotions and menu highlights' :
                   improvementPercent <= 15 ? '🚀 Aggressive: Requires dedicated campaign and incentives' :
                   '🔥 Very ambitious: Full team effort with significant changes'}
                </p>
              </div>

              {/* Projected Impact */}
              <div className="bg-[#FDB913]/10 rounded-lg p-4 border border-[#FDB913]/30">
                <h4 className="text-[#b8860b] font-medium mb-3">Projected Impact</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Additional Orders/{periodLabel}</span>
                    <span className="text-gray-900 font-bold">
                      +{(selectedPairing.order_count * improvementPercent / 100).toFixed(0)} orders
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Revenue Increase/{periodLabel}</span>
                    <span className="text-[#b8860b] font-bold text-lg">
                      +{formatPrice(calculateRevenueImpact(selectedPairing.order_count, selectedPairing.drink_price))}
                    </span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#FDB913]/30">
                    <span className="text-gray-700">Annual Impact</span>
                    <span className="text-green-600 font-bold text-lg">
                      +{formatPrice(
                        calculateRevenueImpact(selectedPairing.order_count, selectedPairing.drink_price) *
                        (days <= 7 ? 52 : days <= 30 ? 12 : 4)
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Items */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="text-gray-900 font-medium mb-2">Recommended Actions</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>💡 Train servers to suggest {selectedPairing.drink_name} with {selectedPairing.food_name}</li>
                  <li>📋 Add pairing suggestion to menu or table tents</li>
                  <li>💰 Consider a small discount when ordered together</li>
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default CustomerPreferenceInsights;